/**
 * Payment Cancel Page - Trade Loop v2.0
 *
 * Displayed when user cancels Stripe checkout
 * - Informs user checkout was canceled
 * - Provides options to retry or return to plans
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { XCircle, ArrowLeft, CreditCard, HelpCircle, Smartphone } from 'lucide-react';

export default function PaymentCancelPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Set by the hand-off redeem endpoint when checkout started in the native app. Mirrors
  // PaymentSuccessPage: a browser that got here from the app is a dead end otherwise. The
  // default page offers "Return to Dashboard", which is a WEB dashboard this person never came
  // from and may not even be signed into — while the thing they actually want, going back to the
  // app, was only reachable via the browser's own back affordance in the corner.
  const cameFromApp = searchParams.get('fromApp') === '1';

  const handleReturnToPlans = () => {
    navigate('/plans');
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleContactSupport = () => {
    // TODO: Implement support contact functionality
    window.location.href = 'mailto:support@gotradeloop.com?subject=Payment%20Question';
  };

  // Came from the app: the ONLY thing most people want here is to go back to it, so that is the
  // centred primary action and everything else is stripped out. No AppLayout — this browser is
  // very likely signed out, or signed in as someone else, and app chrome it cannot use is noise.
  if (cameFromApp) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
              <XCircle className="w-10 h-10 text-amber-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-3">Checkout canceled</h1>
            <p className="text-gray-700 mb-6">
              No charges were made and your plan hasn&rsquo;t changed.
            </p>

            <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Smartphone className="w-6 h-6 text-blue-600" />
              </div>
              <p className="font-semibold text-gray-900 mb-1">Close this tab and reopen Trade Loop</p>
              <p className="text-sm text-gray-600">
                You can start the upgrade again from Plans whenever you&rsquo;re ready.
              </p>
            </div>

            {/* Secondary, and deliberately worded as "here": finishing in this browser means
                signing in, which is the thing the hand-off existed to avoid. */}
            <Button variant="ghost" size="sm" onClick={handleReturnToPlans} className="text-blue-600">
              Or view plans in this browser
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AppLayout activeTab="profile" title="Checkout Canceled">
      <div className="max-w-2xl mx-auto p-4">
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
          <CardContent className="p-12 text-center">
            {/* Cancel Icon */}
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
              <XCircle className="w-10 h-10 text-amber-600" />
            </div>

            {/* Cancel Message */}
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Checkout Canceled</h1>
            <p className="text-lg text-gray-700 mb-2">
              Your payment was not processed
            </p>
            <p className="text-gray-600 mb-8">
              No charges have been made to your account. You can try again whenever you're ready.
            </p>

            {/* Information Box */}
            <div className="bg-white rounded-lg p-6 mb-8 text-left max-w-md mx-auto">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-blue-600" />
                What Happened?
              </h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>You canceled the checkout process before completing payment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Your account remains on your current plan with no changes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>You can upgrade anytime by visiting the Plans page</span>
                </li>
              </ul>
            </div>

            {/* Benefits Reminder */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-8 text-left max-w-md mx-auto">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Still Interested in Upgrading?
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                Our premium plans help you grow your referral network faster with:
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                  <span>More referrals and connections per month</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                  <span>Priority customer support</span>
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button
                onClick={handleReturnToPlans}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                View Plans & Pricing
              </Button>
              <Button variant="outline" onClick={handleGoHome}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Dashboard
              </Button>
            </div>

            {/* Support Link */}
            <div className="pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-3">
                Have questions about our plans or need help?
              </p>
              <Button variant="ghost" size="sm" onClick={handleContactSupport} className="text-blue-600">
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
